import { useState, useEffect } from 'react'
import gql from 'graphql-tag'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, FormFeedback, Row, Col, Alert, Form, UncontrolledTooltip } from 'reactstrap'
import { useQuery, useMutation } from '@apollo/client'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import Select from 'react-select'

import toTitleCase from '../../Functions/toTitleCase'
import { PeopleSchema } from './IncidentModalsSchema'
import SearchPlaces from '../../Material/SearchPlaces'
import './Modals.css'
import AddRepresentativeModal from './AddRepresentativeModal'

const CreateIncidentParty = gql`
    mutation createIncidentParty($input: CreateIncidentPartyMutationInput!){
        createIncidentParty(input:$input){
            ok
            errors{
                field
                messages
            }
        }
    }
`

const CreateIncidentPartyPeople = gql`
    mutation createIncidentPartyPeople($input: CreateIncidentPartyPeopleMutationInput!){
        createIncidentPartyPeople(input:$input){
            ok
            errors{
                field
                messages
            }
        }
    }
`

const INJURIES_ENUM_QUERY = gql`
    query GetInjuriesChoices {
        injuriesChoices: __type(name: "IncidentsIncidentPartyPeopleInjuriesChoices") {
            enumValues {
                name
                description
            }
        }
        
    }
`

const ROLES_ENUM_QUERY = gql`
    query GetRolesChoices {
        rolesChoices: __type(name: "IncidentsIncidentPartyPeopleRoleChoices") {
            enumValues {
                name
                description
            }
        }
    }
`

const ALL_INCIDENT_PARTY_REPRESENTATIVES = gql`
  query AllIncidentPartyRepresentatives {
    allIncidentPartyRepresentatives {
      edges {
        node {
          id
          name
          businessName
        }
      }
    }
  }
`

const AddPeopleModal = ({ isOpen, toggle, partyType, incidentId, partyId, role, roles, refetchQuery, onCreate }) => {
    const [error, setError] = useState(null)
    const [partyName, setPartyName] = useState('')
    const { data: injuriesData, loading: injuriesLoading } = useQuery(INJURIES_ENUM_QUERY)
    const { data: rolesData, loading: rolesLoading } = useQuery(ROLES_ENUM_QUERY)

    const roleOptions = rolesData?.rolesChoices?.enumValues?.filter(opt => role ? opt.name === role : roles && Array.isArray(roles) ? roles.includes(opt.name) : true).map(opt => ({
        value: opt.name,
        label: opt.description || opt.name,
    })) || []
    const [createIncidentParty] = useMutation(CreateIncidentParty)
    const [createIncidentPartyPeople] = useMutation(CreateIncidentPartyPeople)
    const { data: representativesData, refetch: refetchRepresentatives } = useQuery(ALL_INCIDENT_PARTY_REPRESENTATIVES)

    const { handleSubmit, reset, control, setValue, formState: { errors, isSubmitting } } = useForm({
        resolver: yupResolver(PeopleSchema),
        defaultValues: {
            role: role || partyType,
            name: '',
            phoneNumber: '',
            email: '',
            address: '',
            driverLicense: '',
            injuries: '',
            statement: '',
            isBusinessEntity: false,
            representativeId: null,
        }
    })

    const [representativeOptions, setRepresentativeOptions] = useState([])
    const [isRepresentativeModalOpen, setRepresentativeModalOpen] = useState(false)
    useEffect(() => {
        const options = representativesData?.allIncidentPartyRepresentatives?.edges?.map(edge => ({
            label: edge.node.businessName + " - " + edge.node.name,
            value: edge.node.id,
        })) || []
        setRepresentativeOptions(options)
    }, [representativesData])

    const onSubmit = async (data) => {
        setError(null)
        let input = {}
        if (onCreate) {
            onCreate(data)
            return
        }
        if (incidentId && (!partyName || partyName === "" || partyName === null)) {
            setError("Party Title is required")
            return
        }
        if (incidentId) {
            input = {
                incidentId,
                partyType,
                partyName: partyName,
                partyPeople: [{ ...data, representativeId: data.representativeId }],
            }
        } else if (partyId) {
            input = { ...data, incidentPartyId: partyId, representativeId: data.representativeId }
        }

        try {
            let res = partyId
                ? await createIncidentPartyPeople({ variables: { input } })
                : await createIncidentParty({ variables: { input } })
            if (incidentId) {
                if (res.data?.createIncidentParty?.ok) {
                    if (refetchQuery) refetchQuery()
                    reset()
                    toggle()
                } else if (res.data?.createIncidentParty?.errors[0].messages[0]) {
                    setError(res.data?.createIncidentParty?.errors[0].messages[0])
                } else {
                    setError(res.data?.createIncidentParty?.errors)
                }
            } else if (res.data?.createIncidentPartyPeople?.ok) {
                if (refetchQuery) refetchQuery()
                reset()
                setPartyName('')
                toggle()
            } else if (res.data?.createIncidentPartyPeople?.errors[0].messages[0]) {
                setError(res.data?.createIncidentPartyPeople?.errors[0].messages[0])
            } else {
                setError(res.data?.createIncidentPartyPeople?.errors)
            }
        } catch (err) {
            setError('An unexpected error occurred. ' + err.message)
        }
    }

    const injuryOptions = injuriesData?.injuriesChoices?.enumValues?.map(opt => ({
        value: opt.name,
        label: opt.description || opt.name
    })) || []

    const closeAndReset = () => {
        reset()
        setPartyName('')
        toggle()
    }

    const toggleRepresentativeModal = () => setRepresentativeModalOpen(!isRepresentativeModalOpen)

    const handleNewRepresentative = (newRepresentative) => {
        const newOption = { label: newRepresentative.name, value: newRepresentative.id }
        setRepresentativeOptions((prevOptions) => {
            const exists = prevOptions.some(option => option.value === newOption.value)
            if (!exists) {
                return [...prevOptions, newOption]
            }
            return prevOptions
        })
        setValue("representativeId", newOption.value)
    }

    return (
        <>
            <Modal className='add-people-modal modal-dialog-centered' isOpen={isOpen} toggle={closeAndReset} size="lg">
                <ModalHeader>Add {toTitleCase(roles && Array.isArray(roles) ? roles[0] : role || partyType)}</ModalHeader>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalBody>
                        {error && <Row><Col><Alert color="danger">{error}</Alert></Col></Row>}
                        {!partyId &&
                            <Row><Col>
                                <FormGroup>
                                    <Label for="partyName">Party Title*</Label>
                                    <Input
                                        id="partyName"
                                        type="text"
                                        placeholder={(partyType === "WITNESS" || role === "WITNESS") ? "e.g., John Doe Witnesses, ABC Store Employees, etc." : (partyType === "OTHER" || role === "OTHER") ? "e.g., Property, Local Bike Club, etc." : (partyType === "CYCLIST" || role === "CYCLIST") ? "e.g., John Doe Cyclists, Local Bike Club, etc." : (partyType === "NON_MOTORIST" || role === "NON_MOTORIST") ? "e.g., Pedestrian, Bicyclist, etc." : (partyType === "UNAUTHORIZED_DRIVER" || role === "UNAUTHORIZED_DRIVER") ? "e.g., Joyride Driver, Stolen Vehicle Driver, etc." : (partyType === "VEHICLE_DRIVER" || role === "VEHICLE_DRIVER") ? "e.g., John Doe, Jane Smith, etc." : ""}
                                        value={partyName}
                                        onChange={(e) => { setPartyName(e.target.value) }}
                                        invalid={!partyName || partyName === "" || partyName === null}
                                    />
                                </FormGroup>
                            </Col></Row>}
                        <Row>
                            <Col>
                                <FormGroup>
                                    <Label for="role">Role*</Label>
                                    <Controller
                                        name="role"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                className="bos-custom-select"
                                                classNamePrefix="bos-select"
                                                {...field}
                                                options={roleOptions}
                                                isLoading={rolesLoading}
                                                isClearable
                                                placeholder="Select role"
                                                onChange={(option) => field.onChange(option ? option.value : '')}
                                                value={roleOptions.find(opt => opt.value === field.value) || null}
                                            />
                                        )}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <Label for="name">Person Name*</Label>
                                    <Controller
                                        name="name"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                placeholder="Enter person name"
                                                {...field}
                                                invalid={!!errors.name}
                                            />
                                        )}
                                    />
                                    <FormFeedback>{errors.name?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <Label for="phoneNumber">Phone Number</Label>
                                    <Controller
                                        name="phoneNumber"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                placeholder="Enter phone number"
                                                {...field}
                                                invalid={!!errors.phoneNumber}
                                            />
                                        )}
                                    />
                                    <FormFeedback>{errors.phoneNumber?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <Label for="email">Email</Label>
                                    <Controller
                                        name="email"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="email"
                                                placeholder="Enter email"
                                                {...field}
                                                invalid={!!errors.email}
                                            />
                                        )}
                                    />
                                    <FormFeedback>{errors.email?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <Label for="injuries">Injuries</Label>
                                    <Controller
                                        name="injuries"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                className="bos-custom-select"
                                                classNamePrefix="bos-select"
                                                {...field}
                                                options={injuryOptions}
                                                isLoading={injuriesLoading}
                                                isClearable
                                                placeholder="Select injuries"
                                                onChange={(option) => field.onChange(option ? option.value : '')}
                                                value={injuryOptions.find(opt => opt.value === field.value) || null}
                                            />
                                        )}
                                    />
                                    <FormFeedback>{errors.injuries?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <Label for="driverLicense">Driver License</Label>
                                    <Controller
                                        name="driverLicense"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                placeholder="Enter driver license"
                                                {...field}
                                                invalid={!!errors.driverLicense}
                                            />
                                        )}
                                    />
                                    <FormFeedback>{errors.driverLicense?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>
                        <FormGroup>
                            <Label for="representative">Representative</Label>
                            <div className="d-flex align-items-end gap-2 representative-field-row">
                                <div className="flex-grow-1 representative-field-input">
                                    <Controller
                                        name="representativeId"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                className="bos-custom-select"
                                                classNamePrefix="bos-select"
                                                {...field}
                                                options={representativeOptions}
                                                isClearable
                                                placeholder="Select Representative"
                                                onChange={(option) => field.onChange(option ? option.value : null)}
                                                value={representativeOptions.find(opt => opt.value === field.value) || null}
                                            />
                                        )}
                                    />
                                </div>
                                <Button className="add-rep-btn" id="representativeAddTooltip" onClick={toggleRepresentativeModal}><i className="fa fa-plus" /></Button>
                                <UncontrolledTooltip target="representativeAddTooltip" placement="top">Add New Representative</UncontrolledTooltip>
                            </div>
                        </FormGroup>
                        <FormGroup>
                            <Label for="address">Address</Label>
                            <Controller
                                name="address"
                                control={control}
                                render={({ field }) => (
                                    <SearchPlaces
                                        value={field.value ?? ""}
                                        searchType="address"
                                        onSaveAction={(item) => field.onChange(item?.name ?? "")}
                                    />
                                )}
                            />
                            <FormFeedback>{errors.address?.message}</FormFeedback>
                        </FormGroup>

                        <FormGroup check className='mb-3'>
                            <Controller
                                name="isBusinessEntity"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        type="checkbox"
                                        id="isBusinessEntity"
                                        {...field}
                                        checked={field.value || false}
                                    />
                                )}
                            />
                            <Label for="isBusinessEntity" check className="ms-2">Is Business Entity</Label>
                            <FormFeedback>{errors.isBusinessEntity?.message}</FormFeedback>
                        </FormGroup>

                        <FormGroup>
                            <Label for="statement">Statement</Label>
                            <Controller
                                name="statement"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        type="textarea"
                                        placeholder="Enter statement"
                                        {...field}
                                        invalid={!!errors.statement}
                                    />
                                )}
                            />
                            <FormFeedback>{errors.statement?.message}</FormFeedback>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={closeAndReset} disabled={isSubmitting}>Cancel</Button>
                        <Button color="primary" type="submit" disabled={isSubmitting}>{isSubmitting ? "Loading..." : "Add"}</Button>
                    </ModalFooter>
                </form>
            </Modal>
            {isRepresentativeModalOpen &&
                <AddRepresentativeModal
                    isOpen={isRepresentativeModalOpen}
                    toggle={toggleRepresentativeModal}
                    refetchRepresentatives={refetchRepresentatives}
                    onCreate={handleNewRepresentative}
                />
            }
        </>
    )
}

export default AddPeopleModal
